import React, { useState, useEffect, useCallback, useRef } from 'react';
import config from '../config';
import '../styles/HomeDashboard.css';

/**
 * HomeDashboard v8
 *
 * Changes vs v7:
 *  • handleRefresh now triggers the Lambda asynchronously via the backend
 *    and POLLS GET /api/dashboard/home every 5s until the dashboard's
 *    generated_at advances beyond what it was when Refresh was clicked.
 *  • Refresh button stays spinning the entire time the poll loop runs.
 *  • Hard timeout at 90s — falls back to whatever data is currently there.
 */
const HomeDashboard = ({ authToken, onNewChat, onNoAccess }) => {
  const [dashboard, setDashboard]   = useState(null);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hasAccess, setHasAccess]   = useState(null);
  const [isAdmin, setIsAdmin]       = useState(false);
  const [formatMode, setFormatMode] = useState('compact');
  const [activeTab, setActiveTab]   = useState('primary');

  // Guards against state updates after unmount during the long poll loop
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // ── Helper: just GET /home and update dashboard ───────────
  const fetchHomeOnce = useCallback(async () => {
    const res  = await fetch(`${config.apiBaseUrl}/api/dashboard/home`, {
      headers: { 'Authorization': `Bearer ${authToken}` },
    });
    return res.json();
  }, [authToken]);

  // ── Initial load ──────────────────────────────────────────
  const loadInitial = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchHomeOnce();
      if (!mountedRef.current) return;
      if (data.success) {
        setHasAccess(data.has_access);
        setIsAdmin(!!data.is_admin);
        if (data.has_access && data.dashboard) {
          setDashboard(data.dashboard);
        } else if (!data.has_access) {
          if (onNoAccess) onNoAccess();
        }
      } else {
        setHasAccess(false);
        if (onNoAccess) onNoAccess();
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      if (mountedRef.current) {
        setHasAccess(false);
        if (onNoAccess) onNoAccess();
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchHomeOnce]);

  useEffect(() => { loadInitial(); }, [loadInitial]);

  // ── Refresh: trigger Lambda, then poll ────────────────────
  const handleRefresh = async () => {
    if (refreshing || !isAdmin) return;
    setRefreshing(true);

    const POLL_INTERVAL_MS = 5000;
    const TIMEOUT_MS = 90000;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    try {
      // 1. Trigger backend refresh (which async-invokes the Lambda)
      const triggerRes = await fetch(`${config.apiBaseUrl}/api/dashboard/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });
      const triggerData = await triggerRes.json();

      if (triggerRes.status === 403) {
        setIsAdmin(false);
        return;
      }
      if (!triggerData.success) {
        console.error('Refresh trigger failed:', triggerData);
        return;
      }

      const previousAt = triggerData.previous_generated_at;

      // If the backend didn't actually invoke the Lambda (env not configured
      // or AWS error), there's no new data coming — just reload once.
      if (!triggerData.lambda_invoked) {
        console.info('Lambda not invoked:', triggerData.lambda_message);
        const data = await fetchHomeOnce();
        if (mountedRef.current && data.success && data.dashboard) {
          setDashboard(data.dashboard);
        }
        return;
      }

      // 2. Poll until generated_at advances OR we hit the timeout
      const startedAt = Date.now();
      while (mountedRef.current && Date.now() - startedAt < TIMEOUT_MS) {
        await sleep(POLL_INTERVAL_MS);
        if (!mountedRef.current) return;

        try {
          const data = await fetchHomeOnce();
          if (data.success && data.dashboard) {
            const newAt = data.dashboard.generated_at;
            const advanced = newAt && (!previousAt || String(newAt) > String(previousAt));
            if (advanced) {
              setDashboard(data.dashboard);
              return;
            }
          }
        } catch (e) {
          // Transient — keep polling
          console.warn('Poll iteration failed:', e);
        }
      }

      // 3. Timeout — load whatever is there now
      console.warn('Refresh polling timed out after 90s; showing latest available.');
      const fallback = await fetchHomeOnce();
      if (mountedRef.current && fallback.success && fallback.dashboard) {
        setDashboard(fallback.dashboard);
      }
    } catch (err) {
      console.error('Refresh error:', err);
    } finally {
      if (mountedRef.current) setRefreshing(false);
    }
  };

  if (loading || hasAccess === null) {
    return <div className="hd-loading"><div className="hd-loader" /></div>;
  }
  if (!hasAccess || !dashboard) return null;

  const primary   = dashboard.primary   || { mtd: {}, ytd: {} };
  const secondary = dashboard.secondary || { mtd: {}, ytd: {} };
  const insights  = dashboard.insights  || {};

  let dataDateStr = '';
  if (dashboard.data_as_of) {
    const d = new Date(dashboard.data_as_of);
    if (!isNaN(d.getTime())) {
      dataDateStr = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    }
  }
  if (!dataDateStr && dashboard.generated_for_date) {
    const d = new Date(dashboard.generated_for_date);
    if (!isNaN(d.getTime())) {
      d.setDate(d.getDate() - 1);
      dataDateStr = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    }
  }

  return (
    <div className="hd-container">
      <div className="hd-header">
        <div className="hd-header-left">
          {isAdmin && (
            <button
              className={`hd-refresh-btn ${refreshing ? 'hd-refresh-btn--spinning' : ''}`}
              onClick={handleRefresh}
              disabled={refreshing}
              title={refreshing
                ? 'Regenerating from Snowflake — this can take up to 40s'
                : 'Refresh dashboard (regenerates from Snowflake)'}
              aria-label="Refresh dashboard"
            >
              <RefreshIcon spinning={refreshing} />
            </button>
          )}
          <div className="hd-header-text">
            <h2 className="hd-title">📊 Sales Dashboard</h2>
            <span className="hd-subtitle">
              {refreshing
                ? 'Regenerating from Snowflake — please wait…'
                : 'Primary vs Secondary — YTD & MTD'}
            </span>
          </div>
        </div>

        <div className="hd-header-right">
          {dataDateStr && <span className="hd-date">Data as of {dataDateStr}</span>}
          <FormatToggle mode={formatMode} onChange={setFormatMode} />
          <button className="hd-new-chat-btn" onClick={onNewChat}>➕ New Chat</button>
        </div>
      </div>

      <div className="hd-tabs" role="tablist" aria-label="Sales channel">
        <button
          role="tab" aria-selected={activeTab === 'primary'}
          className={`hd-tab ${activeTab === 'primary' ? 'hd-tab--active hd-tab--primary' : ''}`}
          onClick={() => setActiveTab('primary')}
        >
          <span className="hd-tab-dot hd-tab-dot--blue" />
          Primary Sales
        </button>
        <button
          role="tab" aria-selected={activeTab === 'secondary'}
          className={`hd-tab ${activeTab === 'secondary' ? 'hd-tab--active hd-tab--secondary' : ''}`}
          onClick={() => setActiveTab('secondary')}
        >
          <span className="hd-tab-dot hd-tab-dot--green" />
          Secondary Sales
        </button>
      </div>

      {activeTab === 'primary' ? (
        <div className="hd-metrics-grid hd-metrics-grid--primary">
          <MetricCard
            icon="🔵" title="Volume — 8OZ" unit="8OZ"
            mtd={primary.mtd?.oz8} ytd={primary.ytd?.oz8}
            mtdLabel={primary.mtd?.period_label} ytdLabel={primary.ytd?.period_label}
            format="oz8" mode={formatMode} accent="primary" />
          <MetricCard
            icon="📦" title="Volume — Physical Cases" unit="Cs"
            mtd={primary.mtd?.vol_cs} ytd={primary.ytd?.vol_cs}
            mtdLabel={primary.mtd?.period_label} ytdLabel={primary.ytd?.period_label}
            format="number" mode={formatMode} accent="primary" />
          <MetricCard
            icon="💰" title="Sales Value" unit="₹" unitPosition="prefix"
            mtd={primary.mtd?.sales_val} ytd={primary.ytd?.sales_val}
            mtdLabel={primary.mtd?.period_label} ytdLabel={primary.ytd?.period_label}
            format="currency" mode={formatMode} accent="primary" />
          <MetricCard
            icon="🏪" title="Unique Billed Distributors" unit=""
            mtd={primary.mtd?.uniq_dist} ytd={primary.ytd?.uniq_dist}
            mtdLabel={primary.mtd?.period_label} ytdLabel={primary.ytd?.period_label}
            format="count" mode={formatMode} accent="primary" />
        </div>
      ) : (
        <div className="hd-metrics-grid hd-metrics-grid--secondary">
          <MetricCard
            icon="📦" title="Volume — Physical Cases" unit="Cs"
            mtd={secondary.mtd?.vol_cs} ytd={secondary.ytd?.vol_cs}
            mtdLabel={secondary.mtd?.period_label} ytdLabel={secondary.ytd?.period_label}
            format="number" mode={formatMode} accent="secondary" />
          <MetricCard
            icon="💰" title="Sales Value" unit="₹" unitPosition="prefix"
            mtd={secondary.mtd?.sales_val} ytd={secondary.ytd?.sales_val}
            mtdLabel={secondary.mtd?.period_label} ytdLabel={secondary.ytd?.period_label}
            format="currency" mode={formatMode} accent="secondary" />
          <MetricCard
            icon="🛒" title="Unique Billed Retailers" unit=""
            mtd={secondary.mtd?.uniq_retailers} ytd={secondary.ytd?.uniq_retailers}
            mtdLabel={secondary.mtd?.period_label} ytdLabel={secondary.ytd?.period_label}
            format="count" mode={formatMode} accent="secondary" />
        </div>
      )}

      {(insights.ytd_volume_insights || insights.red_flags || insights.top_actions) && (
        <div className="hd-insights">
          <h3 className="hd-insights-title">🤖 AI-Powered Daily Insights</h3>
          <div className="hd-insights-grid">
            {insights.ytd_volume_insights && (
              <InsightCard icon="📈" title="YTD Volume Insights" variant="volume"
                content={insights.ytd_volume_insights} />
            )}
            {insights.red_flags && (
              <InsightCard icon="🚨" title="Red Flags" variant="flags"
                content={insights.red_flags} />
            )}
            {insights.top_actions && (
              <InsightCard icon="⚡" title="Top 3 Actions" variant="actions"
                content={insights.top_actions} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// Sub-components & helpers (unchanged from v7)
// ═══════════════════════════════════════════════════════════════

const MetricCard = ({
  icon, title, unit, unitPosition = 'suffix',
  mtd, ytd, mtdLabel, ytdLabel,
  format, mode, accent,
}) => {
  const hasData = (mtd && mtd.cur !== undefined) || (ytd && ytd.cur !== undefined);
  return (
    <div className={`hd-metric-card hd-metric-card--${accent}`}>
      <div className="hd-metric-header">
        <span className="hd-metric-icon">{icon}</span>
        <span className="hd-metric-title">{title}</span>
      </div>
      {!hasData ? (
        <div className="hd-metric-empty">No data available</div>
      ) : (
        <table className="hd-metric-table">
          <thead>
            <tr>
              <th className="hd-th-period">Period</th>
              <th>CY</th>
              <th>LY</th>
              <th className="hd-th-growth">Growth</th>
            </tr>
          </thead>
          <tbody>
            <MetricRow
              periodLabel={mtdLabel ? `MTD ${shortMonth(mtdLabel)}` : 'MTD'}
              data={mtd} unit={unit} unitPosition={unitPosition}
              format={format} mode={mode} />
            <MetricRow
              periodLabel={ytdLabel ? `YTD ${shortYtd(ytdLabel)}` : 'YTD'}
              data={ytd} unit={unit} unitPosition={unitPosition}
              format={format} mode={mode} />
          </tbody>
        </table>
      )}
    </div>
  );
};

const MetricRow = ({ periodLabel, data, unit, unitPosition, format, mode }) => {
  if (!data || data.cur === undefined) {
    return (
      <tr>
        <td className="hd-td-period">{periodLabel}</td>
        <td colSpan="3" className="hd-td-empty">—</td>
      </tr>
    );
  }
  const curStr  = withUnit(formatValue(data.cur,  format, mode), unit, unitPosition);
  const prevStr = withUnit(formatValue(data.prev, format, mode), unit, unitPosition);
  const curP    = splitValueUnit(curStr);
  const prevP   = splitValueUnit(prevStr);
  return (
    <tr>
      <td className="hd-td-period">{periodLabel}</td>
      <td className="hd-td-value">
        <span className="hd-num">{curP.num}</span>
        {curP.unit && <span className="hd-unit"> {curP.unit}</span>}
      </td>
      <td className="hd-td-value hd-td-prev">
        <span className="hd-num">{prevP.num}</span>
        {prevP.unit && <span className="hd-unit"> {prevP.unit}</span>}
      </td>
      <td className="hd-td-growth"><GrowthBadge pct={data.growth_pct} /></td>
    </tr>
  );
};

const GrowthBadge = ({ pct }) => {
  if (pct === null || pct === undefined) return <span className="hd-growth-neutral">—</span>;
  const num = Number(pct);
  if (isNaN(num)) return <span className="hd-growth-neutral">—</span>;
  if (num === 0)  return <span className="hd-growth-neutral">0%</span>;
  const isPositive = num > 0;
  const cls   = isPositive ? 'hd-growth-positive' : 'hd-growth-negative';
  const sign  = isPositive ? '+' : '';
  const arrow = isPositive ? '▲' : '▼';
  return (
    <span className={cls}>
      <span className="hd-growth-arrow">{arrow}</span>
      {sign}{num.toFixed(2)}%
    </span>
  );
};

const InsightCard = ({ icon, title, variant, content }) => (
  <div className={`hd-insight-card hd-insight-card--${variant}`}>
    <div className="hd-insight-card-header">
      <span className="hd-insight-icon">{icon}</span>
      <span className="hd-insight-title">{title}</span>
    </div>
    <ul className="hd-insight-list">
      {cleanAndSplit(content).map((line, i) => (
        <li key={i} className="hd-insight-item">{line}</li>
      ))}
    </ul>
  </div>
);

const FormatToggle = ({ mode, onChange }) => (
  <div className="hd-format-toggle" role="group" aria-label="Number format">
    <button
      className={`hd-toggle-btn ${mode === 'compact' ? 'hd-toggle-btn--active' : ''}`}
      onClick={() => onChange('compact')}
      title="Show values as Bn / Cr / Mn">Compact</button>
    <button
      className={`hd-toggle-btn ${mode === 'absolute' ? 'hd-toggle-btn--active' : ''}`}
      onClick={() => onChange('absolute')}
      title="Show full absolute values">Absolute</button>
  </div>
);

const RefreshIcon = ({ spinning }) => (
  <svg
    className={spinning ? 'hd-spin' : ''}
    width="16" height="16" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round"
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

function formatValue(val, format, mode) {
  if (val === null || val === undefined) return '—';
  const num = Number(val);
  if (isNaN(num)) return String(val);
  if (num === 0)  return '0';
  if (mode === 'absolute') return num.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  if (format === 'count')  return num.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  if (format === 'currency') {
    if (num >= 1e9) return (num / 1e9).toFixed(2) + ' Bn';
    if (num >= 1e7) return (num / 1e7).toFixed(2) + ' Cr';
    if (num >= 1e5) return (num / 1e5).toFixed(2) + ' L';
    return num.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }
  if (num >= 1e6) return (num / 1e6).toFixed(2) + ' Mn';
  if (num >= 1e3) return (num / 1e3).toFixed(1) + ' K';
  return num.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function withUnit(str, unit, position) {
  if (!unit || str === '—') return str;
  return position === 'prefix' ? `${unit}${str}` : `${str} ${unit}`;
}

function splitValueUnit(str) {
  if (!str || typeof str !== 'string') return { num: String(str || ''), unit: '' };
  const idx = str.indexOf(' ');
  if (idx === -1) return { num: str, unit: '' };
  return { num: str.slice(0, idx), unit: str.slice(idx + 1) };
}

function shortMonth(periodLabel) {
  if (!periodLabel) return '';
  const m = periodLabel.match(/^([A-Za-z]+)/);
  return m ? m[1] : '';
}
function shortYtd(periodLabel) {
  if (!periodLabel) return '';
  const m = periodLabel.match(/^([A-Za-z]+-[A-Za-z]+)/);
  return m ? m[1] : '';
}

function cleanAndSplit(text) {
  if (!text) return [];
  let cleaned = text
    .replace(/^.*?(RED FLAGS|TOP 3 ACTIONS FOR TODAY|YTD SALES VOLUME INSIGHTS)\s*\*{0,2}\s*:?\s*/im, '')
    .replace(/\*{2,}/g, '')
    .replace(/[🚩🚨⚡📈📊🔴🟡🟢▶►●○•]/g, '')
    .replace(/Action\s*\d+\s*[-–:]\s*/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  let lines = cleaned.split(/\n/).map(l => l.trim()).filter(l => l.length > 3);
  if (lines.length <= 1 && cleaned.length > 100) {
    lines = cleaned.split(/(?<=\.)\s+(?=[A-Z])/).filter(l => l.trim().length > 3);
  }
  return lines.map(l => l.replace(/^\s*[-–•:]\s*/, '').trim()).filter(Boolean);
}

export default HomeDashboard;
